<template>
  <!-- Try it: the visitor flips the app's missed-call auto-reply (Settings → Inbox → Missed-call
       SMS, lib/view/inbox/settings/missed_call_sms_settings.dart; strings
       settingsCommsPhoneMissedCall*) and the conversation below shows what the caller gets.
       It never moves on its own. Both outcomes share one grid cell, so the box never resizes.
       The plan line reads row missedCallTextBack from data/pricing-features.json. -->
  <section class="smc">
    <div class="smc-shell smc-grid">
      <header class="smc-heading">
        <h2>{{ chapter.title }}</h2>
        <p class="smc-intro">{{ chapter.intro }}</p>
        <ul class="smc-points">
          <li v-for="point in chapter.points" :key="point">{{ point }}</li>
        </ul>
        <p class="smc-plan">{{ chapter.planLabel }} <b translate="no">{{ plan.name }}</b> (${{ plan.monthly }}/mo).</p>
      </header>

      <div class="smc-demo">
        <div class="smc-card">
          <div class="smc-setting">
            <span class="smc-setting-copy" translate="no">
              <b>Send auto-reply on missed call</b>
              <small class="smc-swap">
                <span :class="{ 'is-on': on }">Active — every missed call triggers a reply.</span>
                <span :class="{ 'is-on': !on }">Off — callers receive nothing automatically.</span>
              </small>
            </span>
            <button
              type="button"
              class="smc-switch"
              :class="{ 'is-on': on }"
              role="switch"
              :aria-checked="on"
              :aria-label="chapter.switchLabel"
              @click="on = !on"
            ><i /></button>
          </div>
          <div class="smc-field" translate="no">
            <small>Reply message</small>
            <p>Sorry we missed your call! We'll get back to you shortly.</p>
          </div>

          <p class="smc-label">{{ chapter.threadLabel }}</p>
          <div class="smc-thread" translate="no" aria-live="polite">
            <p class="smc-event"><svg viewBox="0 0 24 24" aria-hidden="true"><path :d="missed" /></svg>Missed call · +1 (512) 555-0147 · 9:20</p>
            <div class="smc-swap smc-outcome">
              <div :class="{ 'is-on': on }" :aria-hidden="!on">
                <p class="smc-out">Sorry we missed your call! We'll get back to you shortly.<small>SMS · 9:20</small></p>
                <p class="smc-in">No problem, I'm free after 2pm today.<small>9:24</small></p>
              </div>
              <div :class="{ 'is-on': !on }" :aria-hidden="on">
                <p class="smc-nothing">{{ chapter.offOutcome }}</p>
              </div>
            </div>
          </div>
          <p class="smc-note">{{ chapter.note }}</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { firstPlan } from "~/utils/pricingGates";

defineProps({
  chapter: { type: Object, required: true },
});

const on = ref(true);
const plan = firstPlan("missedCallTextBack");
const missed = "M23.71 16.67C20.66 13.78 16.54 12 12 12 7.46 12 3.34 13.78.29 16.67c-.18.18-.29.43-.29.71 0 .28.11.53.29.71l2.48 2.48c.18.18.43.29.71.29.27 0 .52-.11.7-.28.79-.74 1.69-1.36 2.66-1.85.33-.16.56-.5.56-.9v-3.1c1.45-.48 3-.73 4.6-.73 1.6 0 3.15.25 4.6.72v3.1c0 .39.23.74.56.9.98.49 1.87 1.12 2.66 1.85.18.18.43.28.7.28.28 0 .53-.11.71-.29l2.48-2.48c.18-.18.29-.43.29-.71 0-.27-.11-.52-.29-.7zM21.16 6.26l-1.41-1.41-3.56 3.55 1.41 1.41s3.45-3.52 3.56-3.55zM13 2h-2v5h2V2zM6.4 9.81 7.81 8.4 4.26 4.84 2.84 6.26c.11.03 3.56 3.55 3.56 3.55z";
</script>

<style scoped>
.smc { background: #fff; padding: 6.5rem 0; }
.dark .smc { background: #020617; }
.smc-shell { width: min(100% - 3rem, 84rem); margin: 0 auto; }
.smc-grid { display: grid; gap: 2.5rem; align-items: center; }
@media (min-width: 1024px) { .smc-grid { grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.05fr); gap: 4.5rem; } }
.smc-heading h2 { color: #020617; font-size: clamp(2rem, 4vw, 3.25rem); font-weight: 880; line-height: 1.04; text-wrap: balance; }
.dark .smc-heading h2 { color: #f8fafc; }
.smc-intro { margin-top: 1rem; color: #475569; font-size: 1rem; line-height: 1.7; }
.dark .smc-intro { color: #cbd5e1; }
.smc-points { display: grid; gap: 0.5rem; margin-top: 1.25rem; padding: 0; list-style: none; }
.smc-points li { position: relative; padding-left: 1rem; color: #334155; font-size: 0.95rem; line-height: 1.55; }
.dark .smc-points li { color: #cbd5e1; }
.smc-points li::before { content: ""; position: absolute; top: 0.6em; left: 0; width: 6px; height: 6px; border-radius: 2px; background: #1ba7ce; }
.smc-plan { margin-top: 1.25rem; color: #475569; font-size: 0.92rem; }
.dark .smc-plan { color: #cbd5e1; }

.smc-demo {
  border-radius: 14px;
  background:
    radial-gradient(circle, rgb(0 0 0 / 0.08) 0.8px, transparent 1.3px) 7px 7px / 14px 14px,
    linear-gradient(135deg, rgb(124 155 224 / 0.12), rgb(159 139 230 / 0.06));
  padding: 2rem;
}
.dark .smc-demo { background: radial-gradient(circle, rgb(255 255 255 / 0.1) 0.8px, transparent 1.3px) 7px 7px / 14px 14px, linear-gradient(135deg, rgb(124 155 224 / 0.12), rgb(159 139 230 / 0.05)); }
.smc-card {
  max-width: 480px;
  margin: 0 auto;
  border: 1px solid rgb(0 0 0 / 0.1);
  border-radius: 10px;
  background: #fff;
  padding: 1rem 1.1rem 1.1rem;
  color: #171717;
  font-family: "Space Grotesk", Inter, ui-sans-serif, system-ui, sans-serif;
  font-size: 13.5px;
  line-height: 1.45;
  box-shadow: 0 18px 50px -18px rgb(15 23 42 / 0.28);
}
.dark .smc-card { border-color: rgb(255 255 255 / 0.13); background: #1c1c1c; color: #ececec; box-shadow: 0 18px 50px -18px rgb(0 0 0 / 0.7); }
.smc-setting { display: flex; gap: 1rem; align-items: flex-start; justify-content: space-between; border: 1px solid rgb(0 0 0 / 0.1); border-radius: 10px; padding: 0.75rem 0.85rem; }
.dark .smc-setting { border-color: rgb(255 255 255 / 0.13); background: #262626; }
.smc-setting-copy { display: grid; gap: 0.15rem; }
.smc-setting b { font-weight: 600; }
.smc-setting small { color: #5f6368; font-size: 12px; }
.dark .smc-setting small { color: #a8a8a8; }
/* Both texts sit in one cell; only the current one shows, so nothing reflows. */
.smc-swap { display: grid; }
.smc-swap > * { grid-area: 1 / 1; opacity: 0; visibility: hidden; transition: opacity 0.25s ease, visibility 0s linear 0.25s; }
.smc-swap > .is-on { opacity: 1; visibility: visible; transition: opacity 0.3s ease, visibility 0s; }
.smc-switch {
  position: relative;
  flex: none;
  width: 44px;
  height: 26px;
  margin-top: 2px;
  border: 0;
  border-radius: 999px;
  background: #c7cbd1;
  cursor: pointer;
  transition: background 0.2s ease;
}
.smc-switch i { position: absolute; top: 3px; left: 3px; width: 20px; height: 20px; border-radius: 999px; background: #fff; box-shadow: 0 1px 2px rgb(0 0 0 / 0.25); transition: transform 0.2s ease; }
.smc-switch.is-on { background: #171717; }
.smc-switch.is-on i { transform: translateX(18px); }
.dark .smc-switch { background: #4a4a4a; }
.dark .smc-switch.is-on { background: #ececec; }
.dark .smc-switch.is-on i { background: #171717; }
.smc-switch:focus-visible { outline: 2px solid #0e7490; outline-offset: 3px; }
.smc-field { margin-top: 0.6rem; border: 1px solid rgb(0 0 0 / 0.1); border-radius: 8px; padding: 0.5rem 0.75rem; }
.dark .smc-field { border-color: rgb(255 255 255 / 0.13); }
.smc-field small { color: #5f6368; font-size: 11.5px; }
.dark .smc-field small { color: #a8a8a8; }
.smc-field p { margin: 0.1rem 0 0; }
.smc-label { margin-top: 1rem; color: #5f6368; font-size: 12px; font-weight: 500; }
.dark .smc-label { color: #a8a8a8; }
.smc-thread { display: grid; gap: 0.5rem; margin-top: 0.4rem; border-radius: 8px; background: #f4f6f8; padding: 0.75rem; }
.dark .smc-thread { background: #242424; }
.smc-event { display: flex; align-items: center; justify-content: center; gap: 0.35rem; margin: 0; color: #b42318; font-size: 12px; }
.dark .smc-event { color: #f19a8f; }
.smc-event svg { width: 14px; height: 14px; fill: currentColor; }
.smc-outcome > div { display: grid; gap: 0.5rem; align-content: start; }
.smc-out, .smc-in { max-width: 82%; margin: 0; border-radius: 10px; padding: 0.45rem 0.7rem; }
.smc-out { justify-self: end; background: #0f172a; color: #fff; }
.dark .smc-out { background: #f8fafc; color: #0f172a; }
.smc-in { justify-self: start; border: 1px solid rgb(0 0 0 / 0.1); background: #fff; }
.dark .smc-in { border-color: rgb(255 255 255 / 0.13); background: #2a2a2a; }
.smc-out small, .smc-in small { display: block; margin-top: 0.15rem; opacity: 0.7; font-size: 10.5px; }
.smc-nothing { align-self: center; margin: 0; border: 1px dashed rgb(0 0 0 / 0.18); border-radius: 8px; padding: 0.75rem; color: #5f6368; font-size: 12.5px; text-align: center; }
.dark .smc-nothing { border-color: rgb(255 255 255 / 0.2); color: #a8a8a8; }
.smc-note { margin-top: 0.75rem; color: #5f6368; font-size: 12px; }
.dark .smc-note { color: #a8a8a8; }
@media (max-width: 639px) {
  .smc { padding: 4.5rem 0; }
  .smc-shell { width: min(100% - 2rem, 84rem); }
  .smc-demo { padding: 0.9rem; }
}
@media (prefers-reduced-motion: reduce) {
  .smc-swap > *, .smc-switch, .smc-switch i { transition: none; }
}
</style>
